import React, { useState } from 'react';
import { useLocalize } from '@saviynt/common';
import { ButtonIcon, Icon, ListRow } from '@saviynt/design-system';
import PropTypes from 'prop-types';

import useCreateRowObject from '../../hooks/use-createrowobject';
import { msgs } from '../../misc/constants';
import { hasSelectedRoles, isRole, isSession } from '../../utilities';
import ListRowContent from '../ListRow/ListRowContent/ListRowContent';

import '../ListRow/ListRow.css';

const KINDS = {
  ENDPOINT: 'endpoint',
  SESSION: 'session',
  ACCOUNT: 'account',
  ROLE: 'role',
};

function ListRowWrapper({
  kind,
  listItemObj,
  isCheckInModalOpen,
  setIsCheckInModalOpen,
  setIconClickedForRow,
  setCheckInModalKind,
  isSelected,
  listRowOnClick,
  listRowData,
  setListRowData,
  setIsExtendModalOpen,
  onLaunchSession,
  setIsSessionTargetModalVisible,
  setSessionDetailsForAd,
  updateEntityCheckedStatus,
  isJitForApps,
  selectedRoles,
}) {
  const createObjectForRow = useCreateRowObject(
    listItemObj,
    isCheckInModalOpen,
    setIsCheckInModalOpen,
    setIconClickedForRow,
    setCheckInModalKind,
    listRowData,
    setListRowData,
    setIsExtendModalOpen,
    onLaunchSession,
    setIsSessionTargetModalVisible,
    setSessionDetailsForAd,
    isJitForApps
  );
  const [isHovered, setIsHovered] = useState(false);
  const localize = useLocalize();

  const listItem = createObjectForRow();
  const { title, description, status, primaryIcon, moreOptionsIcon } = listItem;

  const isUserOnBulkSelectMode = hasSelectedRoles(selectedRoles);

  const matchingRole = selectedRoles?.find(
    (roles) => roles.roleKey === listItemObj.roleKey
  );

  // List Row Center Content
  const renderedCenterContent = (
    <ListRowContent title={title} description={description} />
  );

  // List Row Trailing Content
  const renderSecondaryIcon = () => {
    const secondaryIcon =
      kind !== KINDS.ENDPOINT &&
      !isSelected(listItemObj) &&
      listItem.secondaryIcon;

    if (secondaryIcon) {
      return <div className='ListRow-icon'>{secondaryIcon}</div>;
    }

    // for sessions, show a blank icon for alignment.
    if (listItem.type === KINDS.SESSION) {
      return <div className='ListRow-icon Listrow-iconBlank' />;
    }

    return null;
  };

  const buttonIconHandler = (e) => {
    e.stopPropagation();
  };

  const listMoreOptionIcon = (
    <ButtonIcon
      size='large'
      kind='ghost'
      icon={<Icon kind='moreVertical' color='navy-500' />}
      onClick={(e) => buttonIconHandler(e, 'moreOption')}
    />
  );

  const sessionStatus = !isSelected(listItemObj) && status;
  const listPrimaryIcon = !isSelected(listItemObj) && primaryIcon;
  const listMoreOptionsIcon = !isSelected(listItemObj) && moreOptionsIcon;
  const credentialIndicatorIcon =
    !isSelected(listItemObj) && listItem?.credentialIndicatorIcon;

  const renderedTrailingContent = (
    <div
      className='ListRow-sectionRight'
      onClick={() => listRowOnClick(listItemObj)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          listRowOnClick(listItemObj);
        }
      }}
      role='button'
      tabIndex={0}>
      {credentialIndicatorIcon && (
        <div className='ListRow--credentialIndicatorIcon'>
          {credentialIndicatorIcon}
        </div>
      )}
      {sessionStatus && <div className='ListRow-status'>{sessionStatus}</div>}
      {listPrimaryIcon && <div className='ListRow-icon'>{listPrimaryIcon}</div>}
      {renderSecondaryIcon()}
      <div
        className={`${
          listMoreOptionsIcon
            ? 'ListRow-verticalIcon'
            : 'ListRow-verticalIcon-hidden'
        }`}>
        {listMoreOptionsIcon || listMoreOptionIcon}
      </div>
    </div>
  );

  const isMaxBulkRolesSelected = selectedRoles?.length === 10 && !matchingRole;

  const isCheckBoxVisible = () =>
    !isSession(listItemObj) &&
    isRole(listItemObj) &&
    !isJitForApps &&
    (isUserOnBulkSelectMode || isHovered);

  const checkBoxTooltipConfigs = {
    text: localize(msgs.pam.landingPage.maxRolesSelected),
    placement: 'right',
    isPortalEnabled: true,
  };

  return (
    <div
      className={
        isSelected(listItemObj)
          ? 'LandingPage-listRowContainer--isSelected'
          : 'LandingPage-listRowContainer'
      }
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      <ListRow
        className={
          isSelected(listItemObj)
            ? 'LandingPage-listRow--isSelected'
            : 'LandingPage-listRow'
        }
        leadingComp={
          !isCheckBoxVisible() ? (
            <div className='LandingPage-listRow-leadingComp' />
          ) : undefined
        }
        centerContent={renderedCenterContent}
        trailingContent={renderedTrailingContent}
        handleRowClick={() => listRowOnClick(listItemObj)}
        checkBoxTooltipConfigs={
          isMaxBulkRolesSelected && checkBoxTooltipConfigs
        }
        isListItem={false}
        isCheckboxDisabled={!isCheckBoxVisible()}
        isChecked={!!matchingRole}
        handleCheckBoxChange={(e) => {
          if (isMaxBulkRolesSelected) {
            return;
          }

          const isChecked = e.target.checked;

          if (updateEntityCheckedStatus) {
            updateEntityCheckedStatus(listItemObj, isChecked);
          }
        }}
        isRowDetailsOpen={isSelected(listItemObj)}
        isRowConnectTransitionEnabled
      />
    </div>
  );
}

ListRowWrapper.propTypes = {
  kind: PropTypes.string.isRequired,
  isCheckInModalOpen: PropTypes.bool.isRequired,
  setIsCheckInModalOpen: PropTypes.func.isRequired,
  setIconClickedForRow: PropTypes.func.isRequired,
  setCheckInModalKind: PropTypes.func.isRequired,
  isSelected: PropTypes.func.isRequired,
  listRowOnClick: PropTypes.func.isRequired,
  listRowData: PropTypes.shape({
    result: PropTypes.shape({
      userAccesses: PropTypes.instanceOf(Array).isRequired,
    }).isRequired,
    count: PropTypes.number.isRequired,
  }).isRequired,
  setListRowData: PropTypes.func.isRequired,
  setIsExtendModalOpen: PropTypes.func.isRequired,
  onLaunchSession: PropTypes.func.isRequired,
  setIsSessionTargetModalVisible: PropTypes.func.isRequired,
  setSessionDetailsForAd: PropTypes.func.isRequired,
  updateEntityCheckedStatus: PropTypes.func.isRequired,
  listItemObj: PropTypes.shape({
    isChecked: PropTypes.bool,
    roleKey: PropTypes.number,
  }),
  isJitForApps: PropTypes.bool,
  selectedRoles: PropTypes.arrayOf(
    PropTypes.shape({
      roleName: PropTypes.string,
      roleDisplayName: PropTypes.string,
      roleDescription: PropTypes.string,
      platformType: PropTypes.string,
    })
  ),
};
ListRowWrapper.defaultProps = {
  listItemObj: {},
  isJitForApps: false,
  selectedRoles: null,
};

export default ListRowWrapper;
